"use client";

import { LocaleLink, useLocalePathname } from "@i18n/routing";
import { config } from "@repo/config";
import { useLoginModal } from "@saas/auth/components/LoginModalProvider";
import { useSession } from "@saas/auth/hooks/use-session";
import { UserMenu } from "@saas/shared/components/UserMenu";
import { ChromeColorIcon } from "@shared/components/ChromeColorIcon";
import { LocaleSwitch } from "@shared/components/LocaleSwitch";
import { Logo } from "@shared/components/Logo";
import { Button } from "@ui/components/button";
import Link from "next/link";
import {
	NavigationMenu,
	NavigationMenuContent,
	NavigationMenuItem,
	NavigationMenuLink,
	NavigationMenuList,
	NavigationMenuTrigger,
} from "@ui/components/navigation-menu";
import {
	Sheet,
	SheetContent,
	SheetTitle,
	SheetTrigger,
} from "@ui/components/sheet";
import { cn } from "@ui/lib";
import {
	ArrowRightIcon,
	FileTextIcon,
	LayersIcon,
	MenuIcon,
} from "lucide-react";
import { useTranslations } from "next-intl";
import { useEffect, useState } from "react";
import { Suspense } from "react";
import { useDebounceCallback } from "usehooks-ts";

export function NavBar() {
	const t = useTranslations();
	const { user } = useSession();
	const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
	const localePathname = useLocalePathname();
	const [isTop, setIsTop] = useState(true);
	const [isClient, setIsClient] = useState(false);

	// Always call useLoginModal unconditionally
	const loginModal = useLoginModal();

	// Then use its result conditionally
	const showLoginModal =
		isClient && loginModal ? loginModal.showLoginModal : () => {};

	useEffect(() => {
		setIsClient(true);
	}, []);

	const tools = [
		{
			title: t("batchImageToPrompt.title"),
			href: "/batch-image-to-prompt",
			description: t("batchImageToPrompt.shortDescription"),
			icon: LayersIcon,
			colorClass: "text-slate-600 dark:text-slate-400",
		},
		{
			title: t("aiDescribeImage.title"),
			href: "/ai-describe-image",
			description: t("aiDescribeImage.description"),
			icon: FileTextIcon,
			colorClass: "text-blue-600 dark:text-blue-400",
		},
	];

	const debouncedScrollHandler = useDebounceCallback(
		() => {
			setIsTop(window.scrollY <= 10);
		},
		150,
		{
			maxWait: 150,
		},
	);

	useEffect(() => {
		window.addEventListener("scroll", debouncedScrollHandler);
		debouncedScrollHandler();
		return () => {
			window.removeEventListener("scroll", debouncedScrollHandler);
		};
	}, [debouncedScrollHandler]);

	useEffect(() => {
		setMobileMenuOpen(false);
	}, [localePathname]);

	const isDocsPage = localePathname.startsWith("/docs");

	const menuItems: {
		label: string;
		href: string;
	}[] = [
		{
			label: t("common.menu.home"),
			href: "/",
		},
		{
			label: t("common.menu.pricing"),
			href: "/pricing",
		},
	];

	const isMenuItemActive = (href: string) => {
		if (href === "/") {
			return (
				localePathname === "/" ||
				localePathname === "/ai-describe-image" ||
				localePathname === "/batch-image-to-prompt"
			);
		}
		return localePathname.startsWith(href);
	};

	return (
		<nav
			className={cn(
				"fixed left-0 z-50 w-full transition-all duration-300",
				!isTop || isDocsPage
					? "bg-white/90 shadow-lg backdrop-blur-xl border-b border-indigo-100/50"
					: "bg-gradient-to-b from-white/95 via-indigo-50/30 to-transparent shadow-none",
			)}
			style={{ top: "var(--language-banner-height, 0px)" }}
			data-test="navigation"
		>
			<div className="container">
				<div
					className={cn(
						"flex items-center justify-stretch gap-6 transition-[padding] duration-200",
						!isTop || isDocsPage ? "py-4" : "py-6",
					)}
				>
					<div className="flex flex-1 justify-start">
						<LocaleLink
							href="/"
							className="block hover:no-underline active:no-underline"
						>
							<Logo />
						</LocaleLink>
					</div>

					<div className="hidden flex-1 items-center justify-center lg:flex">
						<NavigationMenu className="relative">
							<NavigationMenuList className="gap-2">
								<NavigationMenuItem>
									<NavigationMenuLink
										asChild
										className={cn(
											"block px-3 py-2 text-sm font-medium transition-colors hover:text-foreground",
											isMenuItemActive("/")
												? "font-bold bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-600 bg-clip-text text-transparent"
												: "text-foreground/80",
										)}
									>
										<LocaleLink href="/">
											{t("common.menu.home")}
										</LocaleLink>
									</NavigationMenuLink>
								</NavigationMenuItem>

								<NavigationMenuItem>
									<NavigationMenuTrigger className="px-3 py-2 text-sm font-medium text-foreground/80 hover:text-foreground">
										{t("common.menu.tools")}
									</NavigationMenuTrigger>
									<NavigationMenuContent>
										<div className="flex flex-col w-[320px] gap-1 p-3">
											{tools.map((tool) => (
												<LocaleLink
													key={tool.title}
													href={tool.href}
													className="flex items-start gap-3 rounded-md p-4 min-h-[44px] leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
												>
													<div className="flex shrink-0 items-center justify-center">
														<tool.icon
															className={`size-4 ${tool.colorClass}`}
														/>
													</div>
													<div className="space-y-1 flex-1">
														<div className="text-sm font-medium leading-none flex items-center gap-1.5">
															{tool.title}
														</div>
														<p className="line-clamp-2 text-xs leading-snug text-muted-foreground">
															{tool.description}
														</p>
													</div>
												</LocaleLink>
											))}
										</div>
									</NavigationMenuContent>
								</NavigationMenuItem>

								{menuItems.slice(1).map((menuItem) => (
									<NavigationMenuItem key={menuItem.href}>
										<NavigationMenuLink
											asChild
											className={cn(
												"block px-3 py-2 text-sm font-medium transition-colors hover:text-foreground",
												isMenuItemActive(menuItem.href)
													? "font-bold text-foreground"
													: "text-foreground/80",
											)}
										>
											<LocaleLink href={menuItem.href}>
												{menuItem.label}
											</LocaleLink>
										</NavigationMenuLink>
									</NavigationMenuItem>
								))}
							</NavigationMenuList>
						</NavigationMenu>
					</div>

					<div className="flex flex-1 items-center justify-end gap-3">
						{/* Chrome Extension Button */}
						<Link
							href="https://chromewebstore.google.com/detail/image-prompt-co/ppdkefgjlgmdhgodncknckbhdmleoond"
							target="_blank"
							rel="noopener noreferrer"
							className="hidden lg:flex"
						>
							<Button
								className="bg-violet-600 hover:bg-violet-700 text-white font-semibold shadow-lg hover:shadow-xl transition-all duration-200 gap-2 px-5 py-2.5 rounded-full flex items-center"
							>
								<ChromeColorIcon className="size-5" />
								<span className="text-[15px]">Add to Chrome for FREE</span>
								<ArrowRightIcon className="size-4 ml-1" />
							</Button>
						</Link>

						{/* Theme toggle button commented out */}
						{/* <ColorModeToggle /> */}
						{config.i18n.enabled && (
							<Suspense fallback={null}>
								<LocaleSwitch />
							</Suspense>
						)}

						<Sheet
							open={mobileMenuOpen}
							onOpenChange={(open) => setMobileMenuOpen(open)}
						>
							<SheetTrigger asChild>
								<Button
									className="lg:hidden min-w-[44px] min-h-[44px]"
									size="icon"
									variant="ghost"
									aria-label="Menu"
								>
									<MenuIcon className="size-4" />
								</Button>
							</SheetTrigger>
							<SheetContent className="w-[280px]" side="right">
								<SheetTitle />
								<div className="flex flex-col items-start justify-center">
									{menuItems.map((menuItem) => (
										<LocaleLink
											key={menuItem.href}
											href={menuItem.href}
											className={cn(
												"block px-3 py-2 font-medium text-base text-foreground/80",
												isMenuItemActive(menuItem.href)
													? "font-bold text-foreground"
													: "",
											)}
										>
											{menuItem.label}
										</LocaleLink>
									))}

									<div className="w-full px-3 py-2">
										<div className="font-medium text-base text-foreground/80">
											{t("common.menu.tools")}
										</div>
										<div className="pl-3 mt-1 flex flex-col">
											{tools.map((tool) => (
												<LocaleLink
													key={tool.title}
													href={tool.href}
													className="block py-2 text-sm text-foreground/80"
												>
													<div className="flex items-center gap-2">
														<tool.icon
															className={`size-4 ${tool.colorClass}`}
														/>
														<span>
															{tool.title}
														</span>
													</div>
												</LocaleLink>
											))}
										</div>
									</div>

									{/* Chrome Extension Button for Mobile */}
									<Link
										href="https://chromewebstore.google.com/detail/image-prompt-co/ppdkefgjlgmdhgodncknckbhdmleoond"
										target="_blank"
										rel="noopener noreferrer"
										className="block px-3 py-2"
										onClick={() => setMobileMenuOpen(false)}
									>
										<Button
											className="w-full bg-violet-600 hover:bg-violet-700 text-white font-semibold shadow-lg hover:shadow-xl transition-all duration-200 gap-2 rounded-full flex items-center justify-center"
										>
											<ChromeColorIcon className="size-5" />
											<span>Add to Chrome for FREE</span>
											<ArrowRightIcon className="size-4 ml-1" />
										</Button>
									</Link>

									{!user && (
										<LocaleLink
											href="/auth/login"
											onClick={() =>
												setMobileMenuOpen(false)
											}
											className="block px-3 py-2 text-base"
										>
											<Button
												variant="ghost"
												className="w-full text-left justify-start"
											>
												{t("common.menu.login")}
											</Button>
										</LocaleLink>
									)}
								</div>
							</SheetContent>
						</Sheet>

						{config.ui.saas.enabled && !user && (
							<LocaleLink href="/auth/login">
								<Button
									key="login"
									className="hidden lg:flex"
									variant="ghost"
								>
									{t("common.menu.login")}
								</Button>
							</LocaleLink>
						)}

						{config.ui.saas.enabled && user && <UserMenu />}
					</div>
				</div>
			</div>
		</nav>
	);
}
